import { z } from 'zod'

export const contactFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  service: z.string().min(1, 'Please select a service'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
})

export type ContactFormData = z.infer<typeof contactFormSchema>

export interface ContactTranslations {
  en: {
    title: string;
    subtitle: string;
    contactInfo: {
      title: string;
      description: string;
      callUs: {
        title: string;
        number: string;
        availability: string;
      };
      emailUs: {
        title: string;
        email: string;
        response: string;
      };
      serviceArea: {
        title: string;
        area: string;
        note: string;
      };
      businessHours: {
        title: string;
        weekdays: string;
        sunday: string;
      };
    };
    services: {
      title: string;
      description: string;
      list: string[];
    };
    whyChooseUs: {
      title: string;
      items: {
        title: string;
        description: string;
      }[];
    };
    payment: {
      title: string;
      description: string;
    };
    form: {
      name: string;
      email: string;
      phone: string;
      service: string;
      message: string;
      submit: string;
      success: string;
      error: string;
    };
  };
  cy: {
    title: string;
    subtitle: string;
    contactInfo: {
      title: string;
      description: string;
      callUs: {
        title: string;
        number: string;
        availability: string;
      };
      emailUs: {
        title: string;
        email: string;
        response: string;
      };
      serviceArea: {
        title: string;
        area: string;
        note: string;
      };
      businessHours: {
        title: string;
        weekdays: string;
        sunday: string;
      };
    };
    services: {
      title: string;
      description: string;
      list: string[];
    };
    whyChooseUs: {
      title: string;
      items: {
        title: string;
        description: string;
      }[];
    };
    payment: {
      title: string;
      description: string;
    };
    form: {
      name: string;
      email: string;
      phone: string;
      service: string;
      message: string;
      submit: string;
      success: string;
      error: string;
    };
  };
}

export const contactTranslations: ContactTranslations = {
  en: {
    title: "Get in Touch",
    subtitle: "Ready to transform your garden? Contact me today for a free consultation.",
    contactInfo: {
      title: "Contact Information",
      description: "We're here to help with all your gardening needs. Reach out to us through any of these channels.",
      callUs: {
        title: "Call Us",
        number: "+447879643396",
        availability: "Available 7 days a week"
      },
      emailUs: {
        title: "Email Us",
        email: "jcgardening21@outlook.com",
        response: "I'll respond within 24 hours"
      },
      serviceArea: {
        title: "Service Area",
        area: "Barry and surrounding areas",
        note: "Free quotes for all South Wales postcodes"
      },
      businessHours: {
        title: "Business Hours",
        weekdays: "Monday - Saturday: 8am - 6pm",
        sunday: "Sunday: 10am - 4pm"
      }
    },
    services: {
      title: "Services We Offer",
      description: "Professional garden services tailored to your needs",
      list: [
        "Garden Design",
        "Lawn Care",
        "Planting",
        "Hardscaping",
        "Garden Maintenance",
        "Tree Services"
      ]
    },
    whyChooseUs: {
      title: "Why Choose Us",
      items: [
        {
          title: "Free Consultation",
          description: "No obligation, expert advice"
        },
        {
          title: "Satisfaction Guaranteed",
          description: "100% customer satisfaction"
        },
        {
          title: "Local Experts",
          description: "15+ years of experience"
        }
      ]
    },
    payment: {
      title: "Easy Payment Options",
      description: "Payment accepted by cash, BACS (bank transfer), or card for your convenience."
    },
    form: {
      name: "Name",
      email: "Email",
      phone: "Phone",
      service: "Service Required",
      message: "Message",
      submit: "Send Message",
      success: "Thank you for your message! I'll get back to you soon.",
      error: "There was an error sending your message. Please try again."
    }
  },
  cy: {
    title: "Cysylltwch â mi",
    subtitle: "Yn barod i drawsnewid eich gardd? Cysylltwch â mi heddiw am ymgynghoriad am ddim.",
    contactInfo: {
      title: "Gwybodaeth Cyswllt",
      description: "Rwy'n yma i helpu gyda'ch holl anghenion garddio. Cysylltwch â mi drwy unrhyw un o'r sianeli hyn.",
      callUs: {
        title: "Ffoniwch Fi",
        number: "+447879643396",
        availability: "Ar gael 7 diwrnod yr wythnos"
      },
      emailUs: {
        title: "E-bostiwch Fi",
        email: "jcgardening21@outlook.com",
        response: "Byddaf yn ymateb o fewn 24 awr"
      },
      serviceArea: {
        title: "Ardal Gwasanaeth",
        area: "Barri a'r ardaloedd cyfagos",
        note: "Dyfynbris am ddim ar gyfer pob cod post yn De Cymru"
      },
      businessHours: {
        title: "Oriau Busnes",
        weekdays: "Dydd Llun - Dydd Sadwrn: 8am - 6pm",
        sunday: "Dydd Sul: 10am - 4pm"
      }
    },
    services: {
      title: "Gwasanaethau Rwy'n eu Cynnig",
      description: "Gwasanaethau garddio proffesiynol wedi'u teilwra i'ch anghenion",
      list: [
        "Dylunio Gardd",
        "Gofal Lawnt",
        "Plannu",
        "Calchwaith",
        "Cynnal a Chadw Gardd",
        "Gwasanaethau Coed"
      ]
    },
    whyChooseUs: {
      title: "Pam Fi",
      items: [
        {
          title: "Ymgynghoriad Am Ddim",
          description: "Dim rhwymedigaeth, cyngor arbenigol"
        },
        {
          title: "Bodlonrwydd Gwarantedig",
          description: "100% bodlonrwydd cwsmeriaid"
        },
        {
          title: "Arbenigwyr Lleol",
          description: "15+ mlynedd o brofiad"
        }
      ]
    },
    payment: {
      title: "Opsiynau Talu Hawdd",
      description: "Derbynnir taliad arian parod, BACS (trosglwyddiad banc), neu gerdyn er eich cyfleustra."
    },
    form: {
      name: "Enw",
      email: "E-bost",
      phone: "Ffôn",
      service: "Gwasanaeth Angenrheidiol",
      message: "Neges",
      submit: "Anfon Neges",
      success: "Diolch am eich neges! Byddaf yn cysylltu â chi yn fuan.",
      error: "Bu gwall wrth anfon eich neges. Rhowch gynnig arall arni."
    }
  }
} 